"use client";

// A company post — title, dek, byline, then the letter.
//
// ONE image is part of the article: it sits INLINE, in the reading column, right
// under the headline, at its own aspect ratio and uncropped. A SET of images is a
// gallery, and a gallery rides the right rail (sticky, slightly transparent) with
// a swiper on mobile. The rail was doing both jobs, so a post with a single image
// — every weekly letter — had its one picture parked in the margin beside the
// prose instead of inside it.
//
// Either way a tap opens the expanded viewer (MediaModal): the page fades away
// and the ground takes the media's own color.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import OrganismBackdrop from "../OrganismBackdrop";
import SiteMenu, { MENU_ORG, MENU_ORG_SCALE } from "../SiteMenu";
import { useIsNarrow } from "./CompanyClient";
import CompanySearch from "./CompanySearch";
import MediaModal from "./MediaModal";
import MediaThumb from "./MediaThumb";
import PostBody from "./PostBody";
import ShareOptionsBar from "../os/artifact/ShareOptionsBar";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import "../stage/surfaces/post.css";
import { type CoTheme } from "../../lib/company/theme";
import { portalForPost } from "../../lib/brand/portal";
import { realMedia } from "../../lib/company/postCardUrl";
import { useCoTheme } from "./useCoTheme";
import { PillButton } from "../ui";
import BrandIcon from "../BrandIcon";
import CellAvatar from "../CellAvatar";
import { organismFromSeed } from "../../lib/organism/identity";
import { spaceCss } from "../../lib/spaces/identity";
import { relTime, type OsNode } from "../../lib/os/tree";
import type { MenuSpace } from "../../lib/nav/siteNav";
import type { CompanyPost } from "../../lib/company/types";
import { isPublicKind } from "../../lib/company/types";
import type { CompanyViewer } from "../../lib/company/data";
import type { Crumb } from "../BrandCrumb";
import { readerOrg, READER_ORG_WIDE, FULL_BLOOM_AGE } from "../../lib/os/readerOrganism";
import DoorMark from "../brand/DoorMark";

/**
 * A page that is a post in SHAPE (title · dek · byline · letter · gateway) but
 * not in STREAM — a comparison under /company/compare. It hands in the parts
 * the stream used to decide: the crumb, the ≡ space, where the share link and
 * the raw door point, and an optional lead that sits above the title (the two
 * app icons a comparison opens on). ONE reader, never a second implementation.
 */
export interface PostFrame {
  trail: Crumb[];
  /** the room this page is read in — ground + organism. Defaults to the room
   *  the post's own kind belongs to; a comparison hands in the OTHER product's
   *  colour so the page feels written about them. */
  theme?: CoTheme;
  /** something that floats on the creature's side of the screen, above it —
   *  a comparison's two app icons. Desktop only; it stands inline on a phone. */
  float?: React.ReactNode;
  /** which company section this page belongs to — the ≡ dots it. (A key, not a
   *  MenuSpace: the space carries handlers, and a server page cannot hand a
   *  function to a client component.) */
  section: "updates" | "blog" | "compare" | "inside" | "reports";
  sharePath: string;
  rawHref: string;
  rest: { href: string; label: string };
  lead?: React.ReactNode;
  /** the byline's "in …" — defaults to the post's category */
  where?: string;
  /** the byline's date, spoken, in place of "3d" — a comparison says when it
   *  was last checked against the other product ("checked 4 november 2026") */
  stamp?: string;
}

export default function PostClient({
  post,
  viewer,
  author = null,
  frame,
}: {
  post: CompanyPost;
  viewer: CompanyViewer | null;
  /** the writer resolved to a real account — their display name and their photo. */
  author?: { name: string; avatarUrl: string | null } | null;
  frame?: PostFrame;
}) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = useIsNarrow();
  // on a phone the creature goes to the bottom centre and back UNDER the veil —
  // there is no field beside the words to stand in (lib/os/readerOrganism).
  const org = readerOrg(isNarrow, READER_ORG_WIDE);
  // the room, shaded to THIS letter. A letter is read in the blog's colour, a
  // release in the updates', a comparison in the other product's own (handed in
  // by the frame) — and within its room each post carries its own shade, which
  // is the exact colour its thumbnail is painted in (law_portal, lib/brand/portal).
  const theme = frame?.theme ?? portalForPost(post).theme;
  useCoTheme(theme.ground);


  // the fading reader: as you scroll into the letter, ramp --art-ramp 0→1 so the
  // .co-read-scrim frosts the organism away (the artifacts reader law). The
  // fold band is the house default now (lib/os/readRamp) — this reader is where
  // it was tuned, and every other reader inherited it. On the stage it hears the
  // stage's scroller (components/stage/useStage).
  useStageReadRamp(320, "art");

  const founder = !!viewer?.founder;
  // which stream this post belongs to: releases → updates, letters → blog, the
  // dispatch archive → inside innernet, a reading of the company → reports.
  const stream =
    post.kind === "update" ? "updates" : post.kind === "blog" ? "blog" : post.kind === "report" ? "reports" : "inside";
  const kindLabel = stream === "updates" ? "updates" : stream === "blog" ? "blog" : stream === "reports" ? "reports" : "inside innernet";
  const kindHref =
    stream === "updates" ? "/company?tab=updates" : stream === "blog" ? "/company" : stream === "reports" ? "/company?tab=reports" : "/company?tab=inside";
  const section: PostFrame["section"] = frame?.section ?? stream;
  // the crumb collapses the (long) post title to its category/subcategory — e.g.
  // "product dispatch" — so the breadcrumb never blows out. Generic buckets fall
  // back to the stream label.
  const crumbTail = (
    post.subcategory ||
    (post.category && !["news", "inside", "internal", "release"].includes(post.category) ? post.category : "") ||
    kindLabel
  ).replace(/[-_]/g, " ");
  // the post's REAL media only. Its own generated card can be filed on the row
  // (the weekly letter carried it as media[0]) and it is never article media:
  // a picture whose only content is the title, sitting under the title. The
  // card is the index's and the unfurl's; inside the page there is the head.
  const media = realMedia(post);
  // one image reads as the article's own; two or more are a gallery, and only a
  // gallery earns the rail
  const gallery = media.length > 1;
  const inlineMedia = media.length === 1 ? media[0] : null;
  // The author's own colour. `company_posts.author` is free text with no user to
  // resolve, so the cell is seeded from the name — a stable colour per writer,
  // and never a second netti minted for a real person (law_one_netti: an
  // author's cell is a mark, not their creature).
  const authorCss = spaceCss(organismFromSeed(post.author || "innernet"));
  // …except when the author IS innernet. The weekly letter is written by the
  // product, not by a person, and a name-seeded gradient made it look like some
  // fourth founder nobody had met. innernet signs with its own mark.
  const byInnernet = (post.author || "").trim().toLowerCase() === "innernet";
  // a public post ends on the gateway; the founder-only streams do not
  const isPublic = isPublicKind(post.kind) && post.status === "published";

  // adapt the post to an OsNode so the shared dashboard ShareOptionsBar (document
  // mode) can act on it — download/copy the markdown, copy link, native share.
  const sharePath = frame?.sharePath ?? `/company/${post.slug}`;
  const postNode: OsNode = {
    key: post.slug,
    path: sharePath,
    kind: "artifact",
    name: post.title,
    symbol: "",
    icon: "page",
    summary: post.dek ?? "",
    stats: [],
    document: post.body_md ?? post.dek ?? post.title,
    createdAt: post.created_at,
    lastActiveAt: post.published_at ?? post.created_at,
  };

  // law_stage — the head is the stage's navigator, a row of its own that the
  // stage folds (fold="split": the head really collapses and the letter's scroll
  // area grows by what it gave up, with the scroll-up reveal). Each collapsing
  // piece publishes its own natural height (the byline row, the dek, and the
  // title, which settles rather than folds).
  const foldRef = useRef<HTMLDivElement>(null);
  const dekRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  // ⚠️ EVERY `.os-lock__fold` NEEDS ITS OWN MEASURE. `max-height` is
  // `calc((1 - fold) * var(--lock-h))`, and with --lock-h unset that calc is
  // invalid, so the property falls back to `none` and the fold never collapses
  // at all. The lead (a comparison's two app icons) had no ref: invisible on
  // desktop, where it is display:none, and 95px of dead head on a phone — which
  // is what pushed the collapsed title down into the body text.
  const leadRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRef, "--lock-h", post.slug);
  useNaturalHeight(dekRef, "--lock-h", post.slug);
  useNaturalHeight(leadRef, "--lock-h", post.slug);
  useNaturalHeight(titleRef, "--lock-h", post.slug);

  // (a drag that starts on the head reaches the letter: the stage relays it,
  // in both of its layouts — lib/stage/scroller relayTouches)

  const openMedia = (i: number) => setExpanded(i);

  return (
    <main className="co-page" style={{ ["--bg" as string]: theme.ground } as React.CSSProperties}>
      <OrganismBackdrop
        // law_focus_pull (2026-10-19): a page that is read rests netti out of focus
        readingFrost
        fixed
        // THE READER LAW (2026-08-10): the words own the left, the creature owns
        // the right. Netti seats at the right edge, half off the page, in FULL
        // BLOOM from the first line to the last (law_full_bloom, 2026-09-26):
        // it no longer grows with the scroll, and nothing frosts or rests it.
        anchor={menuOpen ? MENU_ORG.anchor : org.anchor}
        offset={menuOpen ? MENU_ORG.offset : org.offset}
        glide={menuOpen ? MENU_ORG.glide : undefined}
        glideAxis={menuOpen ? MENU_ORG.glideAxis : undefined}
        size="min(130vh, 980px)"
        blur={0}
        opacity={menuOpen ? MENU_ORG.opacity : 1}
        fade={0.82}
        scale={menuOpen ? (isNarrow ? MENU_ORG_SCALE.narrow : MENU_ORG_SCALE.wide) : 1}
        // ABOVE the veil (z2), like the dashboard — the scrim at z1 frosts the
        // ground it sits on, never the creature itself, and the words at z3
        // still read over both.
        zIndex={menuOpen ? MENU_ORG.zIndex : org.zIndex}
        innerTransMs={menuOpen ? MENU_ORG.innerTransMs : undefined}
        innerEase={menuOpen ? MENU_ORG.innerEase : undefined}
        color={theme.color}
        age={FULL_BLOOM_AGE}
        spin
        publishHue
        live={false}
      />

      <SiteMenu
        // The crumb is the STREAM and its CATEGORY — never the post title. A
        // title is authored text of any length: it ran off the right of a phone
        // and collided with the avatar on a desktop. Dropping the leading
        // "company" too, since the wordmark already says where you are, and
        // skipping the tail when it would only repeat the stream ("blog / blog").
        // COMPANY FIRST, always. Dropping it saved a word and cost the reader
        // the way back out: from a letter or a comparison the trail jumped
        // straight to the stream, so there was no step up to the company itself.
        trail={[
          { label: "company", href: "/company" },
          ...(frame?.trail ?? [
            { label: kindLabel, href: kindHref },
            ...(crumbTail && crumbTail !== kindLabel ? [{ label: crumbTail, truncate: true }] : []),
          ]),
        ]}
        founder={founder}
        current={{
          id: "company",
          label: "company",
          items: [
            { key: "updates", label: "updates", here: section === "updates", onSelect: () => { router.push("/company?tab=updates"); } },
            { key: "blog", label: "blog", here: section === "blog", onSelect: () => { router.push("/company"); } },
            { key: "compare", label: "compare", here: section === "compare", onSelect: () => { router.push("/company/compare"); } },
            { key: "about", label: "about", onSelect: () => { router.push("/company/about"); } },
            { key: "team", label: "team", onSelect: () => { router.push("/company/team"); } },
            ...(founder ? [{ key: "inside", label: "inside innernet", here: section === "inside", onSelect: () => { router.push("/company?tab=inside"); } }] : []),
            ...(founder ? [{ key: "reports", label: "reports", here: section === "reports", onSelect: () => { router.push("/company?tab=reports"); } }] : []),
          ],
        } satisfies MenuSpace}
        hereKey="company"
        search={<CompanySearch />}
        onOpenChange={setMenuOpen}
      />

      {/* the fading reader ground — frosts the GROUND as you read. netti rides
          above it (z2), so the creature stays sharp the whole way down. */}
      <div className="co-read-scrim" aria-hidden />

      {/* the creature's side of the screen, above the creature — a comparison's
          two app icons, breathing. It floats (fixed), so it stays outside the
          stage, over it. */}
      {frame?.float && <div className="co-float" aria-hidden={false}>{frame.float}</div>}

      {/* law_stage — the letter's head is the stage's navigator, a row of its own
          above the letter's scroll area: the byline and the dek fold away, the
          title settles into a bar so you never lose what you're reading, and the
          letter's scroll area grows by what the head gave up (fold="split"). The
          stage folds it, and brings it back on a scroll up. */}
      <Stage
        frame="none"
        fold="split"
        autoFold
        readFocus="always"
        top="clamp(4.6rem, 11vh, 6.4rem)"
        pin="clamp(4.2rem, 8.6vh, 4.75rem)"
        gap="clamp(1.6rem, 4vh, 2.6rem)"
        sceneKey={post.slug}
        dir={1}
        label={post.title}
        rootProps={{ className: "stage--post" }}
        head={
          <div className="co-shell stage-post__head">
            <div className={`co-post${gallery ? "" : " co-post--solo"}`}>
              <div style={{ minWidth: 0 }}>
                <header className="co-post__head">
                  {/* WHO wrote it, first — the same lockup a shared page opens with.
                      A post is somebody's letter; the author belongs above the title,
                      not tucked under it as a footnote. */}
                  <div ref={foldRef} className="os-lock__fold">
                    <div className="share-by co-by os-enter" style={{ ["--i" as string]: 0 }}>
                      {/* three faces, in order of how much we actually know:
                          innernet's own mark · the writer's real photo (resolved
                          against their account, the same one they wear everywhere
                          else) · a colour seeded from the name, when they aren't
                          anyone we can resolve. */}
                      {byInnernet ? (
                        <span className="share-by-cell share-by-cell--mark" aria-hidden>
                          <BrandIcon name="innernet" size={20} />
                        </span>
                      ) : author ? (
                        <CellAvatar name={author.name} src={author.avatarUrl} size={34} fontSize="0.86rem" />
                      ) : (
                        // no profile behind the byline yet — still the one face,
                        // wearing the author's own colour and their initial.
                        <CellAvatar
                          className="share-by-cell"
                          name={post.author}
                          size={34}
                          fontSize="0.86rem"
                          style={{ ["--organism-color" as string]: authorCss.core }}
                        />
                      )}
                      <span className="share-by-line">
                        by {post.author}
                        {(frame?.where ?? post.category) ? ` · in ${(frame?.where ?? post.category).replace(/[-_]/g, " ")}` : ""}
                        {/* "3d" is measured from NOW on both sides of hydration, and
                            this page is cached: the server's minute and the reader's
                            are not the same one, so React threw a text mismatch
                            (#418) on every post page. The time is the same fact
                            either way — let the client's own reading stand. */}
                        {" · "}
                        {frame?.stamp ?? <span suppressHydrationWarning>{relTime(post.published_at ?? post.created_at)}</span>}
                        {post.status === "draft" ? " · draft" : ""}
                        {viewer?.editor && (
                          <>
                            {" · "}
                            <a className="quiet-link" href={`/company/compose?post=${encodeURIComponent(post.slug)}`}>edit</a>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  {/* a comparison opens on its two app icons — the same face its card
                      wore, so the page picks up exactly where the tile left off. It
                      folds away with the byline; the title is what stays. */}
                  {frame?.lead && (
                    <div ref={leadRef} className="os-lock__fold">
                      <div className="co-post__lead os-enter" style={{ ["--i" as string]: 1 }}>{frame.lead}</div>
                    </div>
                  )}
                  <div ref={titleRef} className="os-lock__title">
                    <h1 className="co-post__title os-enter" style={{ fontWeight: 400, margin: 0, ["--i" as string]: 1 }}>{post.title}</h1>
                  </div>
                  {post.dek && (
                    <div ref={dekRef} className="os-lock__fold">
                      <p className="co-post__dek os-enter" style={{ ["--i" as string]: 2 }}>{post.dek}</p>
                    </div>
                  )}
                </header>
              </div>
            </div>
          </div>
        }
      >
        <div className="co-shell stage-post__body">
          <div className={`co-post${gallery ? "" : " co-post--solo"}`}>
            <div style={{ minWidth: 0 }}>
              {/* one image — inside the article, under the headline, uncropped */}
              {inlineMedia && (
                <button
                  type="button"
                  className="co-media co-media--inline"
                  onClick={() => openMedia(0)}
                  aria-label="expand image"
                >
                  <MediaThumb m={inlineMedia} />
                  {(inlineMedia.type === "video" || inlineMedia.type === "youtube") && (
                    <span className="co-media__play" aria-hidden>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
                    </span>
                  )}
                </button>
              )}

              {/* a gallery, on mobile — the swiper rides on top of the reading column */}
              {gallery && (
                <div className="co-mrail">
                  {media.map((m, i) => (
                    <button key={i} type="button" className="co-media" onClick={() => openMedia(i)} aria-label="expand media">
                      <MediaThumb m={m} />
                      {(m.type === "video" || m.type === "youtube") && (
                        <span className="co-media__play" aria-hidden>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <PostBody post={post} />

              {/* the gateway — a public post is read by people who arrived through
                  a link someone handed them, so it ends the way every other public
                  reader ends: one closing line and THE netti pill, no banner, no
                  second CTA (law_gateway). */}
              {isPublic && (
                <section className="gateway" aria-labelledby="co-gate-h">
                  <hr className="share-rule gateway-rule" aria-hidden />
                  <h2 className="gateway-h" id="co-gate-h">this is what innernet remembers. you can keep your own.</h2>
                  <p className="gateway-p">
                    innernet is memory your ai tools read live — projects, decisions, the things
                    you&rsquo;d otherwise re-explain every session. we write these letters out of ours.
                  </p>
                  <div className="gateway-row">
                    <PillButton
                      className="btn-pill--solid"
                      as="a"
                      href={viewer ? "/dashboard" : `/signup?from=blog:${post.slug}`}
                      icon={<DoorMark />}
                    >
                      {viewer ? "open your innernet" : "start your innernet"}
                    </PillButton>
                    {/* the second door: take the letter itself, not just an
                        account. A post is a document, and innernet's whole claim
                        is that a document your ai can read live beats one you
                        re-explain — so the page offers to BE that. */}
                    <a
                      className="quiet-link"
                      href={frame?.rawHref ?? `/company/${encodeURIComponent(post.slug)}/raw`}
                    >
                      save it to your ai
                    </a>
                    <Link className="quiet-link" href={frame?.rest.href ?? "/company"}>{frame?.rest.label ?? "read the rest"}</Link>
                  </div>
                </section>
              )}
            </div>

            {/* desktop — the sticky, slightly-transparent gallery rail */}
            {gallery && (
              <aside className="co-rail">
                {media.map((m, i) => (
                  <button key={i} type="button" className="co-media" onClick={() => openMedia(i)} aria-label="expand media">
                    <MediaThumb m={m} />
                    {(m.type === "video" || m.type === "youtube") && (
                      <span className="co-media__play" aria-hidden>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
                      </span>
                    )}
                  </button>
                ))}
              </aside>
            )}
            {/* no thumbnail in the expanded reader — a post with no media of its
                own simply reads as one centred column over the living organism. */}
          </div>
        </div>
      </Stage>

      {/* the ONE dashboard share/options dock — bottom-left, same size, layout,
          placement and behavior as everywhere else in the product */}
      <ShareOptionsBar
          publicUrl={typeof window === "undefined" ? null : window.location.href.split("#")[0]}
        context={{ mode: "document", page: postNode }}
        projectId={null}
        sharePath={sharePath}
        variant="dock"
      />

      {expanded != null && (
        <MediaModal
          media={media}
          initialIndex={expanded}
          // the title is the page you came from — the viewer crumb names the stream
          crumb={["company", frame ? (frame.trail.at(-1)?.label ?? kindLabel) : kindLabel]}
          onClose={() => setExpanded(null)}
        />
      )}
    </main>
  );
}
